/**
 * Image Uploader Component
 * Drag-and-drop or click-to-upload image uploader for email broadcast
 *
 * Features:
 * - Drag and drop file upload
 * - File type validation (JPG, PNG, GIF, WEBP)
 * - Size validation (max 5MB)
 * - Upload progress indicator
 * - Returns uploaded URL
 *
 * Created: January 4, 2026
 */

import React, { useState, useRef } from "react";
import { Alert, Button, ProgressBar } from "react-bootstrap";
import axios from "axios";
import { toast } from "react-toastify";
import { upload_email_broadcast_image } from "../../data/ApiEndpoints";

const ImageUploader = ({ onUploadComplete, token }) => {
  const [dragging, setDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState(null);
  const fileInputRef = useRef(null);

  // Validate file
  const validateFile = (file) => {
    const allowedTypes = ["image/jpeg", "image/jpg", "image/png", "image/gif", "image/webp"];
    const maxSize = 5 * 1024 * 1024; // 5MB

    if (!allowedTypes.includes(file.type)) {
      return "Invalid file type. Only JPG, PNG, GIF, and WEBP are allowed.";
    }

    if (file.size > maxSize) {
      return "File size exceeds 5MB. Please choose a smaller image.";
    }

    return null;
  };

  // Handle file upload
  const handleUpload = async (file) => {
    const validationError = validateFile(file);
    if (validationError) {
      setError(validationError);
      toast.error(validationError);
      return;
    }

    setError(null);
    setUploading(true);
    setUploadProgress(0);

    const formData = new FormData();
    formData.append("image", file);

    try {
      const response = await axios.post(upload_email_broadcast_image, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(percentCompleted);
        },
      });

      if (response.data.status) {
        toast.success("Image uploaded successfully!");
        if (onUploadComplete) {
          onUploadComplete(response.data.data);
        }
      } else {
        throw new Error(response.data.message || "Upload failed");
      }
    } catch (err) {
      const errorMsg = err.response?.data?.message || err.message || "Upload failed";
      setError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  // Drag and drop handlers
  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragging(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragging(false);

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      handleUpload(files[0]);
    }
  };

  // Click to upload
  const handleClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (e) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      handleUpload(files[0]);
    }
  };

  return (
    <div className="image-uploader">
      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
        onChange={handleFileChange}
        style={{ display: "none" }}
      />

      <div
        className={`upload-zone border rounded p-4 text-center ${
          dragging ? "border-primary bg-light" : "border-secondary"
        } ${uploading ? "pe-none opacity-50" : ""}`}
        style={{
          borderStyle: "dashed",
          borderWidth: "2px",
          minHeight: "200px",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          cursor: uploading ? "not-allowed" : "pointer",
          transition: "all 0.2s",
        }}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        onClick={!uploading ? handleClick : undefined}
      >
        {uploading ? (
          <>
            <div className="mb-3">
              <i className="bi bi-cloud-upload-fill" style={{ fontSize: "48px", color: "#6c757d" }} />
            </div>
            <h6 className="mb-2">Uploading...</h6>
            <div style={{ width: "80%", maxWidth: "300px" }}>
              <ProgressBar now={uploadProgress} label={`${uploadProgress}%`} animated />
            </div>
          </>
        ) : (
          <>
            <div className="mb-3">
              <i
                className="bi bi-cloud-upload"
                style={{ fontSize: "48px", color: dragging ? "#0d6efd" : "#6c757d" }}
              />
            </div>
            <h6 className="mb-2">
              {dragging ? "Drop image here" : "Drag & drop image here"}
            </h6>
            <p className="text-muted mb-3">or</p>
            <Button variant="primary" size="sm">
              Browse Files
            </Button>
            <small className="text-muted mt-3 d-block">
              Supported: JPG, PNG, GIF, WEBP (Max 5MB)
            </small>
          </>
        )}
      </div>

      {error && (
        <Alert variant="danger" className="mt-3 mb-0" dismissible onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
    </div>
  );
};

export default ImageUploader;
